import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GestureResponderEvent, Pressable } from 'react-native';
import { Chat, ChatTypeEnum, FileTypeEnum } from '@matap/shared';
import styled from 'styled-components/native';
import { Box } from '@/components/base/box';
import { ChatVoiceView } from './chat.voice.view';
import { ChatTextView } from './chat.text.view';
import { ChatImageView } from './chat.image.view';
import { ChatPdfView } from './chat.pdf.view';
import { ChatVideoView } from './chat.video.view';
import { AppTextView } from '@/components/base/app.text.view';
import { ChatContainer } from './chat.container';
import * as Clipboard from 'expo-clipboard';
import { menuEmitter } from '@/components/compose/chat/chat.menu';

interface Props {
  chat: Chat;
  temporary: boolean;
  onShowMenu?: (chat: Chat, pageX: number, pageY: number) => void;
  isSentByUser: boolean;
}
function BaseChatView({ chat, temporary, onShowMenu, isSentByUser }: Props) {
  const [isSelected, setIsSelected] = useState(false);

  useEffect(() => {
    const listener = (selectedId: string | null) => {
      setIsSelected(selectedId === chat._id);
    };
    menuEmitter.on('change', listener);
    return () => {
      menuEmitter.off('change', listener);
    };
  }, [chat._id]);

  const Component = useMemo(() => {
    switch (chat.type) {
      case ChatTypeEnum.TEXT:
        return ChatTextView;
      case ChatTypeEnum.FILE: {
        if (!chat.file && !chat.localFile) {
          return FileNotFound;
        }
        const fileType = chat.file?.metadata.type || chat.localFile.type;
        switch (fileType) {
          case FileTypeEnum.IMAGE:
            return ChatImageView;
          case FileTypeEnum.PDF:
            return ChatPdfView;
          case FileTypeEnum.AUDIO:
            return ChatVoiceView;
          case FileTypeEnum.VIDEO:
            return ChatVideoView;
          default:
            return Box;
        }
      }
      default:
        return Box;
    }
  }, [chat]);

  // Handle tap anywhere on the row (text or empty space)
  const handleRowPress = useCallback(
    (e: GestureResponderEvent) => {
      // Note: If you ever want the menu to open for received messages (e.g., to Copy), remove the isSentByUser check here.
      if (isSentByUser && onShowMenu) {
        onShowMenu(chat, e.nativeEvent.pageX, e.nativeEvent.pageY);
      }
    },
    [isSentByUser, onShowMenu, chat],
  );

  const handleLongPress = useCallback(() => {
    if (chat.type === ChatTypeEnum.TEXT) {
      Clipboard.setStringAsync(chat.text);
    }
  }, [chat]);

  return (
    <RowWrapper onPress={handleRowPress} $isSentByUser={isSentByUser} $isSelected={isSelected}>
      <Component isSentByUser={isSentByUser} chat={chat} temporary={temporary} onLongPress={handleLongPress} onPress={handleRowPress} />
    </RowWrapper>
  );
}

function FileNotFound({ isSentByUser }: { chat: Chat; isSentByUser: boolean }) {
  return (
    <ChatContainer isSentByUser={isSentByUser}>
      <FileNotFoundBox width={150} height={70} alignItems={'center'} justifyContent={'center'}>
        <AppTextView text={'فایل حذف شده'} />
      </FileNotFoundBox>
    </ChatContainer>
  );
}

const RowWrapper = styled(Pressable)<{ $isSentByUser: boolean; $isSelected?: boolean }>`
  width: 100%;
  position: relative;
  align-items: ${({ $isSentByUser }) => ($isSentByUser ? 'flex-end' : 'flex-start')};
  background-color: ${({ $isSelected }) => ($isSelected ? 'rgba(0, 0, 0, 0.08)' : 'transparent')};
  padding-horizontal: 10px;
  outline-style: none;
`;

const FileNotFoundBox = styled(Box)`
  margin-top: -10px;
`;

export const ChatView = React.memo(BaseChatView);
